import React, { useState, useEffect } from 'react';
import {
    Plus,
    Search,
    Edit2,
    Trash2,
    Save,
    X,
    User,
    Phone,
    MapPin,
    CreditCard,
    BookOpen,
    Globe,
    Building2,
    Mail
} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { toast } from 'react-hot-toast';

interface Instructor {
    id: string;
    full_name: string;
    private_lesson_price: number | null;
    private_lesson_currency: string | null;
    course_price: number | null;
    course_currency: string | null;
    city: string | null;
    education_language: string | null;
    subjects: string | null;
    phone: string | null;
    account_holder: string | null;
    iban: string | null;
    bank_name: string | null;
    email?: string;
}

interface FormData extends Partial<Instructor> {
    password?: string;
}

const Instructors: React.FC = () => {
    const [instructors, setInstructors] = useState<Instructor[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [editingId, setEditingId] = useState<string | null>(null);

    const [formData, setFormData] = useState<FormData>({
        full_name: '',
        email: '',
        password: '',
        private_lesson_price: null,
        private_lesson_currency: 'TL',
        course_price: null,
        course_currency: 'TL',
        city: '',
        education_language: '',
        subjects: '',
        phone: '',
        account_holder: '',
        iban: '',
        bank_name: ''
    });

    useEffect(() => {
        fetchInstructors();
    }, []);

    const fetchInstructors = async () => {
        try {
            setLoading(true);
            const { data, error } = await supabase
                .from('testprep_teachers')
                .select('*')
                .order('full_name');

            if (error) throw error;
            setInstructors(data || []);
        } catch (error) {
            console.error('Error fetching instructors:', error);
            toast.error('Eğitmenler yüklenirken hata oluştu');
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            if (editingId) {
                // If email or password is provided, update Auth User first
                if (formData.email || formData.password) {
                    const { error: updateError } = await supabase.functions.invoke('update-teacher', {
                        body: {
                            userId: editingId,
                            email: formData.email,
                            password: formData.password,
                            name: formData.full_name
                        }
                    });

                    if (updateError) {
                        const errorMessage = await updateError.context?.json().then((body: any) => body.error).catch(() => updateError.message) || 'Güncelleme hatası';
                        throw new Error(`Hesap güncellenemedi: ${errorMessage}`);
                    }
                }

                const { password, ...updateData } = formData;
                const { error } = await supabase
                    .from('testprep_teachers')
                    .update(updateData)
                    .eq('id', editingId);

                if (error) throw error;
                toast.success('Eğitmen güncellendi');
            } else {
                if (!formData.password || !formData.email) {
                    toast.error('Email ve şifre zorunludur');
                    return;
                }

                // Call Edge Function to create Auth User
                const { data: authData, error: authError } = await supabase.functions.invoke('create-teacher', {
                    body: {
                        email: formData.email,
                        password: formData.password,
                        name: formData.full_name
                    }
                });

                if (authError) {
                    console.error('Edge Function Error:', authError);
                    // Try to extract a meaningful message if possible. 
                    // Supabase functions.invoke returns an error object that might contain context.
                    // But often the body is needed. 
                    // If authError is a FunctionsHttpError, it might have details.
                    const errorMessage = await authError.context?.json().then((body: any) => body.error).catch(() => authError.message) || 'Fonksiyon hatası';
                    throw new Error(`Kullanıcı oluşturulamadı: ${errorMessage}`);
                }

                if (!authData?.user?.id) {
                    throw new Error('Kullanıcı ID alınamadı');
                }

                // Insert into testprep_teachers with specific ID
                const { password, ...insertData } = formData;
                const { error } = await supabase
                    .from('testprep_teachers')
                    .insert([{ ...insertData, id: authData.user.id }]);

                if (error) throw error;
                toast.success('Eğitmen ve hesap oluşturuldu');
            }

            setShowModal(false);
            setEditingId(null);
            setFormData({});
            fetchInstructors();
        } catch (error: any) {
            console.error('Error saving instructor:', error);
            toast.error(error.message || 'Kaydedilirken hata oluştu');
        }
    };

    const handleDelete = async (id: string) => {
        if (!await window.appConfirm('Bu eğitmeni silmek istediğinize emin misiniz?')) return;

        try {
            const { error } = await supabase
                .from('testprep_teachers')
                .delete()
                .eq('id', id);

            if (error) throw error;
            toast.success('Eğitmen silindi');
            fetchInstructors();
        } catch (error) {
            console.error('Error deleting instructor:', error);
            toast.error('Silinirken hata oluştu');
        }
    };

    const handleEdit = (instructor: Instructor) => {
        setFormData(instructor);
        setEditingId(instructor.id);
        setShowModal(true);
    };

    const filteredInstructors = instructors.filter(i =>
        i.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        i.subjects?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        i.city?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="p-6 space-y-6">
            <div className="flex items-center justify-between">
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Eğitmenler</h1>
                <button
                    onClick={() => {
                        setEditingId(null);
                        setFormData({});
                        setShowModal(true);
                    }}
                    className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
                >
                    <Plus className="w-4 h-4" />
                    <span>Yeni Eğitmen</span>
                </button>
            </div>

            {/* Search */}
            <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                    type="text"
                    placeholder="Eğitmen ara..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                />
            </div>

            {/* Table */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm overflow-hidden border border-gray-200 dark:border-gray-700">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm text-left">
                        <thead className="text-xs text-gray-700 dark:text-gray-300 uppercase bg-gray-50 dark:bg-gray-900/50">
                            <tr>
                                <th className="px-6 py-3">Adı Soyadı</th>
                                <th className="px-6 py-3">Dersler</th>
                                <th className="px-6 py-3">Eğitim Dili</th>
                                <th className="px-6 py-3">Şehir</th>
                                <th className="px-6 py-3">İletişim</th>
                                <th className="px-6 py-3">Özel Ders / Kurs</th>
                                <th className="px-6 py-3">Banka Bilgileri</th>
                                <th className="px-6 py-3 text-right">İşlemler</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                            {loading ? (
                                <tr>
                                    <td colSpan={8} className="px-6 py-8 text-center text-gray-500">
                                        Yükleniyor...
                                    </td>
                                </tr>
                            ) : filteredInstructors.length === 0 ? (
                                <tr>
                                    <td colSpan={8} className="px-6 py-8 text-center text-gray-500">
                                        Kayıt bulunamadı.
                                    </td>
                                </tr>
                            ) : (
                                filteredInstructors.map((instructor) => (
                                    <tr key={instructor.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                                        <td className="px-6 py-4 font-medium text-gray-900 dark:text-white">
                                            {instructor.full_name}
                                        </td>
                                        <td className="px-6 py-4 text-gray-600 dark:text-gray-300">
                                            {instructor.subjects || '-'}
                                        </td>
                                        <td className="px-6 py-4 text-gray-600 dark:text-gray-300">
                                            {instructor.education_language || '-'}
                                        </td>
                                        <td className="px-6 py-4 text-gray-600 dark:text-gray-300">
                                            {instructor.city || '-'}
                                        </td>
                                        <td className="px-6 py-4 text-gray-600 dark:text-gray-300">
                                            {instructor.phone || '-'}
                                            {instructor.email && (
                                                <div className="text-xs text-gray-500 mt-1">{instructor.email}</div>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 text-gray-600 dark:text-gray-300">
                                            <div className="flex flex-col">
                                                <span>Özel: {instructor.private_lesson_price ? `${instructor.private_lesson_price} ${instructor.private_lesson_currency || 'TL'}` : '-'}</span>
                                                <span>Kurs: {instructor.course_price ? `${instructor.course_price} ${instructor.course_currency || 'TL'}` : '-'}</span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 text-gray-600 dark:text-gray-300">
                                            <div className="text-xs">
                                                <p>{instructor.bank_name}</p>
                                                <p>{instructor.iban}</p>
                                                <p className="text-gray-400">{instructor.account_holder}</p>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 text-right">
                                            <div className="flex items-center justify-end space-x-2">
                                                <button
                                                    onClick={() => handleEdit(instructor)}
                                                    className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                                >
                                                    <Edit2 className="w-4 h-4" />
                                                </button>
                                                <button
                                                    onClick={() => handleDelete(instructor.id)}
                                                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Modal */}
            {showModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
                    <div className="bg-white dark:bg-gray-900 rounded-xl shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
                        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-800">
                            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                                {editingId ? 'Eğitmen Düzenle' : 'Yeni Eğitmen Ekle'}
                            </h2>
                            <button
                                onClick={() => setShowModal(false)}
                                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <form onSubmit={handleSave} className="p-6 space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Ad Soyad</label>
                                    <div className="relative">
                                        <User className="absolute left-3 top-2.5 text-gray-400 w-4 h-4" />
                                        <input
                                            required
                                            type="text"
                                            value={formData.full_name || ''}
                                            onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                                            className="w-full pl-9 p-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800"
                                        />
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Telefon</label>
                                    <div className="relative">
                                        <Phone className="absolute left-3 top-2.5 text-gray-400 w-4 h-4" />
                                        <input
                                            type="tel"
                                            value={formData.phone || ''}
                                            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                                            className="w-full pl-9 p-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800"
                                        />
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">E-posta</label>
                                    <div className="relative">
                                        <Mail className="absolute left-3 top-2.5 text-gray-400 w-4 h-4" />
                                        <input
                                            required
                                            type="email"
                                            value={formData.email || ''}
                                            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                            className="w-full pl-9 p-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800"
                                        />
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                        {editingId ? 'Şifre (Değiştirmek isterseniz)' : 'Şifre'}
                                    </label>
                                    <div className="relative">
                                        <input
                                            required={!editingId}
                                            type="password"
                                            value={formData.password || ''}
                                            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                                            className="w-full p-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800"
                                            placeholder={editingId ? "Değişiklik yok" : "En az 6 karakter"}
                                        />
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Şehir</label>
                                    <div className="relative">
                                        <MapPin className="absolute left-3 top-2.5 text-gray-400 w-4 h-4" />
                                        <input
                                            type="text"
                                            value={formData.city || ''}
                                            onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                                            className="w-full pl-9 p-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800"
                                        />
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Eğitim Dili</label>
                                    <div className="relative">
                                        <Globe className="absolute left-3 top-2.5 text-gray-400 w-4 h-4" />
                                        <input
                                            type="text"
                                            value={formData.education_language || ''}
                                            onChange={(e) => setFormData({ ...formData, education_language: e.target.value })}
                                            className="w-full pl-9 p-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800"
                                        />
                                    </div>
                                </div>

                                <div className="col-span-2 space-y-2">
                                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Dersler</label>
                                    <div className="relative">
                                        <BookOpen className="absolute left-3 top-2.5 text-gray-400 w-4 h-4" />
                                        <input
                                            type="text"
                                            placeholder="Örn: Matematik, Fizik, İngilizce..."
                                            value={formData.subjects || ''}
                                            onChange={(e) => setFormData({ ...formData, subjects: e.target.value })}
                                            className="w-full pl-9 p-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800"
                                        />
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Özel Ders Fiyatı</label>
                                    <div className="flex gap-2">
                                        <input
                                            type="number"
                                            value={formData.private_lesson_price || ''}
                                            onChange={(e) => setFormData({ ...formData, private_lesson_price: Number(e.target.value) })}
                                            className="w-full p-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800"
                                            placeholder="Fiyat"
                                        />
                                        <select
                                            value={formData.private_lesson_currency || 'TL'}
                                            onChange={(e) => setFormData({ ...formData, private_lesson_currency: e.target.value })}
                                            className="w-24 p-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800"
                                        >
                                            <option value="TL">₺ (TL)</option>
                                            <option value="USD">$ (USD)</option>
                                            <option value="EUR">€ (EUR)</option>
                                            <option value="GBP">£ (GBP)</option>
                                        </select>
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Kurs Fiyatı</label>
                                    <div className="flex gap-2">
                                        <input
                                            type="number"
                                            value={formData.course_price || ''}
                                            onChange={(e) => setFormData({ ...formData, course_price: Number(e.target.value) })}
                                            className="w-full p-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800"
                                            placeholder="Fiyat"
                                        />
                                        <select
                                            value={formData.course_currency || 'TL'}
                                            onChange={(e) => setFormData({ ...formData, course_currency: e.target.value })}
                                            className="w-24 p-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800"
                                        >
                                            <option value="TL">₺ (TL)</option>
                                            <option value="USD">$ (USD)</option>
                                            <option value="EUR">€ (EUR)</option>
                                            <option value="GBP">£ (GBP)</option>
                                        </select>
                                    </div>
                                </div>

                                <div className="col-span-2 border-t border-gray-100 dark:border-gray-800 pt-4 mt-2">
                                    <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                                        <Building2 className="w-4 h-4" />
                                        Banka Bilgileri
                                    </h3>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Banka Adı</label>
                                            <input
                                                type="text"
                                                value={formData.bank_name || ''}
                                                onChange={(e) => setFormData({ ...formData, bank_name: e.target.value })}
                                                className="w-full p-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Hesap Sahibi</label>
                                            <input
                                                type="text"
                                                value={formData.account_holder || ''}
                                                onChange={(e) => setFormData({ ...formData, account_holder: e.target.value })}
                                                className="w-full p-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800"
                                            />
                                        </div>
                                        <div className="space-y-2 col-span-2">
                                            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">IBAN</label>
                                            <div className="relative">
                                                <CreditCard className="absolute left-3 top-2.5 text-gray-400 w-4 h-4" />
                                                <input
                                                    type="text"
                                                    value={formData.iban || ''}
                                                    onChange={(e) => setFormData({ ...formData, iban: e.target.value })}
                                                    className="w-full pl-9 p-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800 font-mono"
                                                    placeholder="TR..."
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-800">
                                <button
                                    type="button"
                                    onClick={() => setShowModal(false)}
                                    className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                                >
                                    İptal
                                </button>
                                <button
                                    type="submit"
                                    className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
                                >
                                    <Save className="w-4 h-4" />
                                    <span>Kaydet</span>
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Instructors;
